import type { DayAvailability, SlotAvailability, ReservationDraft, MyReservation } from "./types";

const WORKER_BASE = (window as any).__RESERVATION_WORKER_BASE__
  ?? "https://empath-line-reservation-worker.sukechii-0623.workers.dev";

// Phase 1.5.5: マルチテナント化対応。
// 当面 Empath LINE 単独運用のため hard-coded。
// 他テナント展開時は URL クエリパラメータ ?account=acc_xxx 等から動的に取得する。
const ACCOUNT_ID = "acc_empath01";

function getIdToken(): string {
  const tok = (window as any).liff?.getIDToken();
  if (!tok) throw new Error("LIFF Token 取得失敗");
  return tok;
}

function authHeaders(): Record<string, string> {
  return {
    "Authorization": `Bearer ${getIdToken()}`,
    "Content-Type": "application/json",
    "X-Account-Id": ACCOUNT_ID,
  };
}

function publicHeaders(): Record<string, string> {
  return { "X-Account-Id": ACCOUNT_ID };
}

export async function fetchMonthAvailability(month: string): Promise<DayAvailability[]> {
  const res = await fetch(`${WORKER_BASE}/api/availability?month=${month}`, { headers: publicHeaders() });
  if (!res.ok) throw new Error("月の空き取得失敗");
  const data = await res.json();
  return data.days as DayAvailability[];
}

export async function fetchDayAvailability(date: string): Promise<SlotAvailability[]> {
  const res = await fetch(`${WORKER_BASE}/api/availability?date=${date}`, { headers: publicHeaders() });
  if (!res.ok) throw new Error("日の空き取得失敗");
  const data = await res.json();
  return data.slots as SlotAvailability[];
}

export async function createReservation(draft: ReservationDraft): Promise<{
  reservation_id: string;
  message: string;
} | { error: string; message: string }> {
  const res = await fetch(`${WORKER_BASE}/api/reservations`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(draft),
  });
  return res.json();
}

// ===== Phase 1.5.3 マイ予約 API =====

export async function fetchMyReservations(): Promise<MyReservation[]> {
  const res = await fetch(`${WORKER_BASE}/api/my-reservations`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`予約確認の取得失敗 (${res.status})`);
  const data = await res.json();
  return (data.reservations ?? []) as MyReservation[];
}

export async function fetchMyReservationDetail(id: string): Promise<MyReservation> {
  const res = await fetch(`${WORKER_BASE}/api/my-reservations/${encodeURIComponent(id)}`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null) as any;
    throw new Error(body?.message ?? `予約詳細の取得失敗 (${res.status})`);
  }
  return await res.json() as MyReservation;
}

export interface CancelMyReservationResponse {
  ok: boolean;
  results: {
    calendar_deleted: boolean;
    customer_pushed: boolean;
    owner_pushed: boolean;
    tag_attached: boolean;
  };
}

export async function cancelMyReservation(
  id: string,
  templateKey: "customer_self" | "other_self",
  freeNote: string
): Promise<CancelMyReservationResponse> {
  const res = await fetch(`${WORKER_BASE}/api/my-reservations/${encodeURIComponent(id)}/cancel`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ template_key: templateKey, free_note: freeNote }),
  });
  const body = await res.json().catch(() => null) as any;
  if (!res.ok) {
    throw new Error(body?.message ?? `キャンセルに失敗しました (${res.status})`);
  }
  return body as CancelMyReservationResponse;
}

// ===== Phase 1.5.4 拡張：日時変更 =====

export interface RescheduleMyReservationResponse {
  ok: boolean;
  new_reservation_id: string;
  results: {
    new_google_calendar_event_id: string | null;
    new_meet_url: string | null;
    old_calendar_deleted: boolean;
    new_calendar_created: boolean;
    customer_pushed: boolean;
    owner_pushed: boolean;
  };
}

/**
 * 既存予約の日時を変更する。
 * 内部的には旧予約のキャンセル + 新予約の作成 + Calendar 更新 + 通知を一括で行う。
 *
 * @param id 旧予約 ID
 * @param newDate "YYYY-MM-DD"
 * @param newTime "HH:MM"
 */
export async function rescheduleMyReservation(
  id: string,
  newDate: string,
  newTime: string
): Promise<RescheduleMyReservationResponse> {
  const res = await fetch(
    `${WORKER_BASE}/api/my-reservations/${encodeURIComponent(id)}/reschedule`,
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ new_date: newDate, new_time: newTime }),
    }
  );
  const body = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(body?.message ?? `予約変更に失敗しました (${res.status})`);
  }
  return body as RescheduleMyReservationResponse;
}
